import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Stack, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import { axiosService } from '../services/axios.service';
import { useSelector } from 'react-redux';
import { StoreState } from '../store/store';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 700,
  margin: '2rem auto',
  textAlign: 'center',
}));

const CancelIconStyled = styled(CancelIcon)(({ theme }) => ({
  fontSize: 80,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

interface PaymentRetryData {
  formData: any;
  provider: string;
  timestamp: number;
  productRequestId: string;
}

const PaymentCancelPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: StoreState) => state.user.loggedInUser);
  const [savedStatus, setSavedStatus] = useState(false);
  const [paymentRetryData, setPaymentRetryData] = useState<PaymentRetryData | null>(null);
  const [selectedPSP, setSelectedPSP] = useState('');
  const [retrying, setRetrying] = useState(false);

  const pspOptions = [
    { value: 'bridgerpay', label: 'BridgerPay (USD)', color: 'primary' as const },
    // { value: 'worldcard', label: 'WorldCard (CAD)', color: 'secondary' as const },
    { value: 'tranzila', label: 'Tranzila (USD)', color: 'info' as const },
  ];
  
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get("status");
    const id = queryParams.get("id");
    const amount = queryParams.get("order_amount");
    const currency = queryParams.get("order_currency");
    const custom_data = queryParams.get("custom_data");
    const product = custom_data ? JSON.parse(custom_data)?.product : "general"

    // Save payment failure status
    if (status && id && !savedStatus) {
      (async () => {
        await axiosService.post("/payment/save", {
          status,
          orderId: id,
          amount,
          currency,
          product,
          userId: user?.id
        });
      })();
      setSavedStatus(true)
    }

    // Retrieve saved payment form data for retry
    const savedRetryData = localStorage.getItem('paymentRetryData');
    if (savedRetryData) {
      try {
        const retryData: PaymentRetryData = JSON.parse(savedRetryData);
        // Check if data is not too old (24 hours)
        const twentyFourHours = 24 * 60 * 60 * 1000;
        if (Date.now() - retryData.timestamp < twentyFourHours) {
          setPaymentRetryData(retryData);
          // Set a different PSP than the one that failed
          const alternativePSP = retryData.provider === 'bridgerpay' ? 'tranzila' : 'bridgerpay';
          setSelectedPSP(alternativePSP);
        } else {
          // Clear old data
          localStorage.removeItem('paymentRetryData');
        }
      } catch (error) {
        console.error('Error parsing payment retry data:', error);
        localStorage.removeItem('paymentRetryData');
      }
    }
  }, [location, savedStatus, user?.id]);

  const handleRetryPayment = async () => {
    if (!paymentRetryData || !selectedPSP) return;

    setRetrying(true);
    try {
      const returnUrl = `${window.location.protocol}//${window.location.host}/payment/success`;
      const cancelUrl = `${window.location.protocol}//${window.location.host}/payment/cancel`;

      const requestPayload = {
        ...paymentRetryData.formData,
        returnUrl,
        cancelUrl,
      };

      const response = await axiosService.post(
        `/payment/create?provider=${selectedPSP}`,
        requestPayload
      );

      if (response.checkout_url) {
        // Update retry data with new PSP
        const newRetryData = {
          ...paymentRetryData,
          provider: selectedPSP,
          timestamp: Date.now(),
        };
        localStorage.setItem('paymentRetryData', JSON.stringify(newRetryData));
        
        window.location.href = response.checkout_url;
      } else {
        throw new Error("No payment URL received from server");
      }
    } catch (error) {
      console.error('Retry payment error:', error);
      setRetrying(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <StyledPaper elevation={3}>
        <CancelIconStyled />
        <Typography variant="h4" component="h1" gutterBottom>
          Payment Cancelled
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Your payment process was cancelled. No charges were made to your account.
        </Typography>

        {paymentRetryData && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Card elevation={0} sx={{ backgroundColor: 'info.light', color: 'info.contrastText', mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RefreshIcon />
                  Quick Retry Available
                </Typography>
                <Typography variant="body2">
                  We saved your payment information. Try again with a different payment processor.
                </Typography>
              </CardContent>
            </Card>

            <Box sx={{ textAlign: 'left', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Payment Details:</strong>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'center' }}>
                <Chip label={`Amount: $${paymentRetryData.formData.amount}`} color="primary" size="small" />
                <Chip label={`Product: ${paymentRetryData.formData.product}`} color="secondary" size="small" />
                <Chip 
                  label={`Failed PSP: ${paymentRetryData.provider}`} 
                  color="error" 
                  size="small" 
                  variant="outlined" 
                />
              </Stack>

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="h6">Choose Alternative Payment Processor</Typography>
                </FormLabel>
                <RadioGroup
                  value={selectedPSP}
                  onChange={(e) => setSelectedPSP(e.target.value)}
                  sx={{ alignItems: 'center' }}
                >
                  {pspOptions
                    .filter(option => option.value !== paymentRetryData.provider)
                    .map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio color={option.color} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentIcon />
                            {option.label}
                            <Chip label="Recommended" size="small" color="success" variant="outlined" />
                          </Box>
                        }
                        sx={{ mb: 1 }}
                      />
                    ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<RefreshIcon />}
                onClick={handleRetryPayment}
                disabled={!selectedPSP || retrying}
                size="large"
              >
                {retrying ? 'Processing...' : 'Retry Payment'}
              </Button>
            </Stack>

            {/* <Alert severity="info" sx={{ textAlign: 'left' }}>
              Your payment information is securely saved for 24 hours to enable quick retry. 
              Different payment processors may have different success rates depending on your location and card type.
            </Alert> */}
          </>
        )}

        <Divider sx={{ my: 3 }} />
        
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant={paymentRetryData ? "outlined" : "contained"}
            color="primary"
            onClick={() => navigate('/payment')}
          >
            Start New Payment
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/dashboard')}
          >
            Return to Dashboard
          </Button>
        </Stack>
      </StyledPaper>
    </Box>
  );
};

export default PaymentCancelPage; 